import React from 'react'
import { Dot, CarouselProvider, Slider, Slide } from 'pure-react-carousel'
import { Helmet } from 'react-helmet-async'
import { Image } from '@deal/components'
import { ExpertProfilePhotoCarouselFragment } from '#src/generated/graphql'
import FallbackPhoto from './fallback.png'
import classnames from 'classnames'
import styles from './styles.css'

interface PhotoProps {
  expertProfile: ExpertProfilePhotoCarouselFragment | null
  className?: string
}

const PhotoCarousel: React.SFC<PhotoProps> = ({ expertProfile, className }) => {
  if (!expertProfile || !expertProfile.profilePhoto) {
    return null
  }

  const galleryPhotos = expertProfile.gallery

  return (
    <>
      {galleryPhotos.length > 0 &&
        galleryPhotos[0].mediaFile && (
          <Helmet>
            <meta property="og:image" content={galleryPhotos[0].mediaFile.url} />
          </Helmet>
        )}
      <div className={classnames(styles.photos, className)}>
        <CarouselProvider
          totalSlides={galleryPhotos.length || 1}
          lockOnWindowScroll={true}
          naturalSlideWidth={370}
          naturalSlideHeight={300}
          isPlaying={true}
          dragEnabled={galleryPhotos.length > 1}
          touchEnabled={galleryPhotos.length > 1}
        >
          <Slider classNameTray={styles.sliderTray}>
            {galleryPhotos.length > 0 ? (
              galleryPhotos.map((photo, index) => {
                if (!photo.mediaFile) {
                  return null
                }

                return (
                  <Slide index={index} className={styles.photoContainer} key={index}>
                    <Image
                      src={photo.mediaFile.url}
                      width={720}
                      height={960}
                      imgixParams={{ fit: 'fill', fill: 'blur', 'fill-blur': 50 }}
                      className={styles.image}
                    />

                    <div
                      className={classnames(styles.gradient, {
                        [styles.hasDots]: galleryPhotos.length > 1,
                        [styles.hasCaption]: photo.caption
                      })}
                    >
                      {photo.caption && <div className={styles.caption}>{photo.caption}</div>}
                    </div>
                  </Slide>
                )
              })
            ) : (
              <Slide index={0} className={styles.photoContainer}>
                <Image
                  src={FallbackPhoto}
                  width={720}
                  height={800}
                  className={styles.placeholderImage}
                />
              </Slide>
            )}
          </Slider>

          {galleryPhotos.length > 1 && (
            <div className={styles.dots}>
              {galleryPhotos.map((_photo, index) => (
                <Dot slide={index} className={styles.dot} key={index}>
                  &nbsp;
                </Dot>
              ))}
            </div>
          )}
        </CarouselProvider>
      </div>
    </>
  )
}

export default PhotoCarousel
